import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, Dictionary, EntityState } from '@reduxjs/toolkit';

import SETTINGS from '#/config/global-settings';

import createSafeContext from '#/utils/createSafeContext';
import { PositionNotification } from '#/api/ws/ws';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';
import {
  getOpenPositions,
  OpenPosition,
  OpenPositionsResp,
  PositionSide,
} from '#/api/positions';
import { usePerpetualMarketsState } from '#/features/perpetuals/market-info/markets-context';
import * as MarketFns from '#/features/market/functions';
import { Market } from '#/api/markets';
import { MarketSummary } from '#/api/markets-summary';
import { useWalletView } from '#/features/wallet/wallet-context';

import { useMarketsSummaryState } from './market-info/markets-summary-context';

import * as PositionFns from './trade-overview/position-functions';

const adapter = createEntityAdapter<OpenPosition>({
  sortComparer: (positionA, positionB) =>
    positionA.market.localeCompare(positionB.market),
});
const selectors = adapter.getSelectors();

const INITIAL_STATE: OpenPositionsState = {
  status: 'connected',
  error: '',
  openPositions: adapter.getInitialState(),
};

export interface OpenPositionsState extends LiveConnectionBaseState {
  readonly openPositions: EntityState<OpenPosition>;
}

export interface OpenPositionsView
  extends Omit<OpenPositionsState, 'openPositions'> {
  readonly openPositions: readonly OpenPositionView[];
  getPosition(id: OpenPosition['id']): OpenPosition | undefined;
  getPositions(): readonly OpenPosition[];
}

export const [useOpenPositionsView, OpenPositionsViewCtx] =
  createSafeContext<OpenPositionsView>('OpenPositionsView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OpenPositionsProvider(props: ProviderProps) {
  const [state, setState] = useState(INITIAL_STATE);
  const { markets } = usePerpetualMarketsState();
  const { marketsSummary } = useMarketsSummaryState();

  const view = useMemo(
    () => createView(state, markets.entities, marketsSummary.entities),
    [markets.entities, marketsSummary.entities, state],
  );

  useOpenPositionsFetch(setState);

  return (
    <OpenPositionsViewCtx value={view}>{props.children}</OpenPositionsViewCtx>
  );
}

export interface OpenPositionView {
  readonly id: string;
  readonly market: string;
  readonly side: PositionSide;
  readonly size: string;
  readonly averageEntryPrice: string;
  readonly averageEntryPriceUsd: string;
  readonly unrealizedPnl: string;
  readonly unrealizedPnlChange: 'up' | 'down';
  readonly fundingPnl: string;
  readonly fundingPnlChange: 'up' | 'down';

  // Extended:
  readonly notional: string;
  readonly markPrice: string;
}

export function createView(
  state: OpenPositionsState,
  markets: Dictionary<Market>,
  marketsSummary: Dictionary<MarketSummary>,
): OpenPositionsView {
  const openPositions = selectors
    .selectAll(state.openPositions)
    .filter((position) => !position.size.eq(0))
    .map((position) => openPositionView(position, markets, marketsSummary));

  return {
    ...state,
    openPositions,
    getPosition: (id: OpenPosition['id']) =>
      selectors.selectById(state.openPositions, id),
    getPositions: () => selectors.selectAll(state.openPositions),
  };
}

function emptyPositionView(id: OpenPosition['id']): OpenPositionView {
  return {
    id,
    market: '-',
    side: 'LONG',
    size: '-',
    notional: '-',
    averageEntryPrice: '-',
    averageEntryPriceUsd: '-',
    markPrice: '-',
    unrealizedPnl: '-',
    unrealizedPnlChange: 'up',
    fundingPnl: '-',
    fundingPnlChange: 'up',
  };
}

function openPositionView(
  position: OpenPosition,
  markets: Dictionary<Market>,
  marketsSummary: Dictionary<MarketSummary>,
): OpenPositionView {
  const market = markets[position.market];
  const marketSummary = marketsSummary[position.market];

  if (market == null || marketSummary == null)
    return emptyPositionView(position.id);

  const quoteCurrencyDecimalPlaces =
    MarketFns.quoteCurrencyDecimalPlaces(market);

  const size = `${position.size.toFormat()} ${market.base_currency}`;

  const notional =
    marketSummary.oracle_price != null
      ? `US$ ${PositionFns.calcNotional(
          position,
          marketSummary.oracle_price,
        ).toFormat(2)}`
      : '-';

  const averageEntryPrice = `${position.average_entry_price.toFormat(
    SETTINGS.tokens.USDC.scale,
  )} ${market.settlement_currency}`;

  const averageEntryPriceUsd = `$${position.average_entry_price_usd.toFormat(
    SETTINGS.fiat.USD.scale,
  )}`;

  const markPrice =
    marketSummary.oracle_price != null
      ? marketSummary.oracle_price.toFormat(quoteCurrencyDecimalPlaces)
      : '-';

  const unrealizedPnl = `${
    position.unrealized_pnl.isPositive() ? '+' : '-'
  }$${position.unrealized_pnl.abs().toFormat(2)}`;

  const unrealizedPnlChange = position.unrealized_pnl.isPositive()
    ? 'up'
    : 'down';

  const fundingPnl = `${
    position.unrealized_funding_pnl.isPositive() ? '+' : '-'
  }$${position.unrealized_funding_pnl.abs().toFormat(2)}`;

  const fundingPnlChange = position.unrealized_funding_pnl.isPositive()
    ? 'up'
    : 'down';

  return {
    id: position.id,
    market: position.market,
    side: position.side,
    size,
    notional,
    averageEntryPrice,
    averageEntryPriceUsd,
    markPrice,
    unrealizedPnl,
    unrealizedPnlChange,
    fundingPnl,
    fundingPnlChange,
  };
}

function useOpenPositionsFetch(
  setState: Dispatch<SetStateAction<OpenPositionsState>>,
): void {
  const walletView = useWalletView();

  const isReady = walletView.isSignedIn;
  const channel = 'positions';

  const fetchAll = useCallback(
    async (signal: AbortSignal) =>
      getOpenPositions({
        signal,
      }),
    [],
  );

  const setAll = useCallback(
    (data: OpenPositionsResp) => {
      setState((state) => ({
        ...state,
        openPositions: adapter.setAll(state.openPositions, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      openPositions: adapter.removeAll(state.openPositions),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: PositionNotification) => {
      setState((state) => {
        switch (notif.data.status) {
          case 'UNKNOWN':
          case 'OPEN':
            return {
              ...state,
              openPositions: adapter.setOne(
                state.openPositions,
                notif.data as OpenPosition,
              ),
            };
          case 'CLOSED':
            return {
              ...state,
              openPositions: adapter.removeOne(
                state.openPositions,
                notif.data.market,
              ),
            };

          // no default
        }
      });
    },
    [setState],
  );

  useLiveConnection<
    OpenPositionsResp,
    OpenPositionsState,
    PositionNotification
  >({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
